import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { useMemo, useState } from 'react';
import { useLazyGetBillableHoursByEmployeeAndMonthReportQuery } from 'store/api/reportingTrends';
import { BillableHoursByEmployeeAndMonthRow, BillableHoursTotals } from 'types/reporting/trends';
import { t } from 'utils/getTranslations';
import { ChartReportView } from '../charts/ChartReportView';
import { EmployeePeriodCharts } from '../charts/EmployeePeriodCharts';
import {
    currentYearSearchParams,
    EMPLOYEE_NAME_SEARCH,
    employeeTrendFilters,
    monthFilter,
    yearFilter,
} from '../charts/trendFilters';
import { formatReportNumber, pivotToneRule, ReportTone } from '../reportCells';
import { ReportEntry } from '../reportCatalog';
import { ReportLayout } from '../ReportLayout';
import { quickFiltersFrom } from '../reportFilters';

const TONE: ReportTone = 'yellow';

type Row = BillableHoursByEmployeeAndMonthRow;

const REPORT_KEY = 'billable-hours-by-employee-and-month';

function billableHours(row: Row): number {
    return row.billableHours;
}

function buildFilterConfig(today: Date): FilterConfig {
    const filters = [yearFilter(today), monthFilter(), ...employeeTrendFilters()];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        quickFilters: quickFiltersFrom(filters, {
            year: 'reporting.filter.year',
            month: 'reporting.filter.month',
            isActive: 'reporting.filter.is-active',
        }),
        searchBarSelectors: EMPLOYEE_NAME_SEARCH,
        filters,
    };
}

function renderCharts(rows: Row[], totals: BillableHoursTotals) {
    return (
        <EmployeePeriodCharts
            tone={TONE}
            rows={rows}
            period="month"
            value={billableHours}
            measure={t('reporting.column.billable-hours')}
            chartTitle={t(`reporting.${REPORT_KEY}.chart`)}
            tableTitle={t(`reporting.${REPORT_KEY}.table`)}
            total={t('reporting.chart.total-hours', { value: formatReportNumber(totals.billableHours) })}
        />
    );
}

export function BillableHoursByEmployeeAndMonthReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetBillableHoursByEmployeeAndMonthReportQuery();
    const [today] = useState(() => new Date());
    const [initialSearchParams] = useState(() => currentYearSearchParams(today));
    const filterConfig = useMemo(() => buildFilterConfig(today), [today]);
    return (
        <ReportLayout
            report={report}
            toneLegend={[{ label: t('reporting.column.billable-hours'), rule: pivotToneRule(TONE) }]}
        >
            <ChartReportView<Row, BillableHoursTotals>
                analyticsSurface={`report-${REPORT_KEY}`}
                data={data}
                isFetching={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                initialSearchParams={initialSearchParams}
            >
                {renderCharts}
            </ChartReportView>
        </ReportLayout>
    );
}
